// Query-rewrite layer (synonym + typo repair). Pure core, no DB, no platform
// imports (CP1) — it lives here so BOTH the backend deals search and the web
// app's client-side name filters run the same matching rules. Synonym
// resolution and typo repair are separate calls because each caller decides
// when repair is allowed: only after the typed terms return zero results.
//
// Synonym lookup: exact match against the caller's synonym rows -> resolves to
// the owning entity's canonical name. The deals search feeds it
// `systemproductsynonyms.normalizedsynonym` (read-only, no synonym storage
// here); other callers may pass an empty list.
//
// Typo repair: when explicitly invoked, run trigram similarity against the
// candidate vocabulary the caller supplies and accept the top match above
// TYPO_SIMILARITY_FLOOR. The vocabulary is injected, so this module is generic
// over the entity being searched — deals pass Category/Attribute/Brand names,
// the store filter passes the discovered store names.
//
// The trigram scoring below is the ONLY implementation — it runs in plain JS
// over the injected vocabulary, with no scoring in SQL. There is no DB wrapper
// for this module and no query-time `pg_trgm` `similarity()` call: an earlier
// header described both, and that claim outlived the code. Keeping this
// accurate matters — BUG53 existed partly because a header advertised Postgres
// full-text matching that the service had never implemented.

/**
 * Similarity floor for typo repair (the commonly-cited `pg_trgm` default for
 * short-word matching). Named constant, tunable post-launch without a design
 * change (Details).
 */
export const TYPO_SIMILARITY_FLOOR = 0.4;

export interface SynonymEntry {
  /** Already-lowercased/trimmed synonym text. */
  normalizedSynonym: string;
  /** The owning entity's canonical (lowercased) name. */
  canonicalName: string;
}

export interface RewriteCatalog {
  synonyms: SynonymEntry[];
  /** The vocabulary repair matches against (lowercased). */
  candidateWords: string[];
}

export interface RewrittenTerm {
  /** The term to actually search on (rewritten, or unchanged if no rewrite applied). */
  term: string;
  /** True iff a synonym or typo rewrite changed the term. */
  corrected: boolean;
  /** The raw token as typed, before any rewrite — for the surface's undo. */
  original: string;
}

/** Extract the 3-character trigrams of a word, padded like `pg_trgm` (two
 * leading blanks + one trailing blank) so edge characters count too.
 *
 * Non-alphanumerics are dropped first, which is what `pg_trgm` does before it
 * builds trigrams. Without that, a typed "kays" scored 0.375 against the store
 * "kay's" — just under the 0.4 floor — so an omitted apostrophe found nothing.
 * Dropping the character (rather than splitting the word on it) is what makes
 * the two forms score identically. */
function trigrams(word: string): Set<string> {
  const padded = `  ${word.replace(/[^a-z0-9]/gi, '')} `;
  const set = new Set<string>();
  for (let i = 0; i < padded.length - 2; i++) {
    set.add(padded.slice(i, i + 3));
  }
  return set;
}

/** Jaccard similarity over trigram sets — the same shape `pg_trgm`'s
 * `similarity()` computes (intersection / union of the two trigram sets). */
function trigramSimilarity(a: string, b: string): number {
  if (a === b) return 1;
  const setA = trigrams(a);
  const setB = trigrams(b);
  if (setA.size === 0 || setB.size === 0) return 0;
  let intersection = 0;
  for (const t of setA) {
    if (setB.has(t)) intersection += 1;
  }
  const union = setA.size + setB.size - intersection;
  return union === 0 ? 0 : intersection / union;
}

/** Resolve only curated synonyms. Search orchestration owns the repair gate. */
export function resolveSynonymTerm(rawTerm: string, catalog: RewriteCatalog): RewrittenTerm {
  const original = rawTerm;
  const normalized = rawTerm.toLowerCase().trim();
  const synonymHit = catalog.synonyms.find((s) => s.normalizedSynonym === normalized);
  if (synonymHit) {
    return { term: synonymHit.canonicalName, corrected: true, original };
  }
  return { term: normalized, corrected: false, original };
}

/**
 * Run typo repair explicitly against the rewrite vocabulary. The caller must
 * first prove that the typed/synonym-resolved query returned zero results.
 */
export function repairTypoTerm(rawTerm: string, catalog: RewriteCatalog): RewrittenTerm {
  const original = rawTerm;
  const normalized = rawTerm.toLowerCase().trim();
  // Already an exact candidate word — no rewrite needed, never runs trigram.
  if (catalog.candidateWords.includes(normalized)) {
    return { term: normalized, corrected: false, original };
  }

  // Typo repair — only reached on zero exact/synonym hit.
  let best: { word: string; score: number } | null = null;
  for (const candidate of catalog.candidateWords) {
    const singularCandidate = candidate.endsWith('s') ? candidate.slice(0, -1) : candidate;
    const score = Math.max(
      trigramSimilarity(normalized, candidate),
      trigramSimilarity(normalized, singularCandidate),
    );
    if (score >= TYPO_SIMILARITY_FLOOR && (!best || score > best.score)) {
      best = { word: candidate, score };
    }
  }
  if (best && !best.word.includes(normalized) && !normalized.includes(best.word)) {
    return { term: best.word, corrected: true, original };
  }

  return { term: normalized, corrected: false, original };
}
